<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page customer-sales-detail-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="日期">
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="出库单号">
                <a-input v-model="query.outboundNo" allow-clear placeholder="销售出库单号" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-input v-model="query.customerKeyword" allow-clear placeholder="客商编号或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-select
                  v-model="query.supplierId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部供应商">
                  <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                    {{ item.supplierCode }} · {{ item.supplierName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="分类">
                <a-tree-select
                  v-model="query.categoryId"
                  allow-clear
                  show-search
                  tree-node-filter-prop="title"
                  :dropdown-style="{ maxHeight: '360px', overflow: 'auto' }"
                  placeholder="全部分类"
                  :tree-data="categoryTreeSelect"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="规格">
                <a-input v-model="query.specKeyword" allow-clear placeholder="SKU编码或规格名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="单位">
                <a-select v-model="query.unitType" @change="search">
                  <a-select-option value="business">销售单位</a-select-option>
                  <a-select-option value="base">基本单位</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>客商销售明细表</h3>
            <p>数据来源为已审核的正常销售出库单明细；金额为价税合计减已审核销售折让，数量按折让同比例折算。「单位」控制销售/基本单位的数量、单价与金额展示。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="customer-sales-detail-amount-total">
              金额合计 <strong class="erp-money">¥{{ formatMoney(amountTotal) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:customer-sales-detail:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无销售明细' }"
          @change="onPageChange">
          <template slot="outboundNo" slot-scope="text, record">
            <a class="customer-sales-detail-link" :title="text || ''" @click="openOutboundDrawer(record)">{{ text }}</a>
          </template>
          <template slot="code" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="qty" slot-scope="text">{{ formatQty(text) }}</template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
          <template slot="empty" slot-scope="value">{{ displayValue(value) }}</template>
        </a-table>
      </section>
    </div>

    <a-drawer
      :visible="outboundDrawerVisible"
      :width="960"
      :closable="false"
      :body-style="{ padding: 0 }"
      class="erp-doc-drawer"
      @close="closeOutboundDrawer">
      <div class="erp-drawer-shell">
        <header class="erp-drawer-header">
          <div>
            <button class="erp-back-button" type="button" aria-label="关闭" @click="closeOutboundDrawer">
              <a-icon type="arrow-left" />
            </button>
            <span class="erp-drawer-kicker">销售出库单</span>
            <h3>{{ outboundForm.outboundNo || '—' }}</h3>
          </div>
          <div><a-button @click="closeOutboundDrawer">关闭</a-button></div>
        </header>
        <div class="erp-drawer-content">
          <a-spin :spinning="outboundLoading">
            <section class="erp-form-card">
              <div class="erp-base-grid">
                <label><span>出库日期</span><a-input :value="outboundForm.outboundDate || '—'" disabled /></label>
                <label><span>审核状态</span><a-input :value="auditStatusText(outboundForm.auditStatus)" disabled /></label>
                <label><span>客商编码</span><a-input :value="outboundForm.customerCode || '—'" disabled /></label>
                <label><span>客商名称</span><a-input :value="outboundForm.customerName || '—'" disabled /></label>
                <label class="wide"><span>备注</span><a-input :value="outboundForm.remark || '—'" disabled /></label>
              </div>
            </section>
            <section class="erp-form-card">
              <div class="erp-section-heading-row"><div><h4>出库明细</h4></div></div>
              <a-table
                :columns="outboundLineColumns"
                :data-source="outboundForm.items || []"
                :pagination="false"
                row-key="id"
                size="small"
                bordered>
                <template slot="sku" slot-scope="text, row">{{ row.skuCode }} · {{ row.skuName }}</template>
                <template slot="qty" slot-scope="text, row">{{ formatLineQty(row) }}</template>
                <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
                <template slot="lineAmount" slot-scope="text, row"><span class="erp-money">¥{{ formatMoney(lineGrossAmount(row)) }}</span></template>
              </a-table>
            </section>
          </a-spin>
        </div>
      </div>
    </a-drawer>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { getSalesOutbound } from '@/api/erp/salesOutbound'
import {
  pageCustomerSalesDetailReport,
  listCustomerSalesDetailSupplierOptions,
  exportCustomerSalesDetailReport
} from '@/api/erp/customerSalesDetailReport'
import { treeProductCategory } from '@/api/erp/productCategory'

const emptyQuery = () => ({
  supplierId: undefined,
  categoryId: undefined,
  customerKeyword: '',
  outboundNo: '',
  specKeyword: '',
  unitType: 'business'
})

export default {
  name: 'ErpCustomerSalesDetailReport',
  data () {
    return {
      query: emptyQuery(),
      dateRange: [],
      supplierOptions: [],
      categoryTreeRaw: [],
      rows: [],
      amountTotal: 0,
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` },
      outboundDrawerVisible: false,
      outboundLoading: false,
      outboundForm: { items: [] },
      outboundLineColumns: [
        { title: 'SKU', scopedSlots: { customRender: 'sku' }, width: 220 },
        { title: '单位', dataIndex: 'unitName', width: 80 },
        { title: '数量', scopedSlots: { customRender: 'qty' }, width: 120 },
        { title: '单价', dataIndex: 'salePrice', scopedSlots: { customRender: 'money' }, width: 100 },
        { title: '价税合计', scopedSlots: { customRender: 'lineAmount' }, width: 100 }
      ]
    }
  },
  computed: {
    categoryTreeSelect () {
      return this.toTreeSelectData(this.categoryTreeRaw)
    },
    tableScroll () {
      const w = this.tableColumns.reduce((sum, col) => sum + (col.width || 0), 0)
      return w > 0 ? { x: w } : undefined
    },
    tableColumns () {
      const fixed = [
        { title: '日期', dataIndex: 'outboundDate', width: 110 },
        { title: '出库单号', dataIndex: 'outboundNo', width: 178, ellipsis: true, scopedSlots: { customRender: 'outboundNo' } },
        { title: '客商编码', dataIndex: 'customerCode', width: 108, ellipsis: true, scopedSlots: { customRender: 'code' } },
        { title: '客商名称', dataIndex: 'customerName', width: 140, scopedSlots: { customRender: 'empty' } },
        { title: '分类', dataIndex: 'categoryName', width: 110, scopedSlots: { customRender: 'empty' } },
        { title: '规格名称', dataIndex: 'specName', width: 110, scopedSlots: { customRender: 'empty' } },
        { title: '规格信息', dataIndex: 'boxSpecDesc', width: 120, scopedSlots: { customRender: 'empty' } }
      ]
      const isBase = this.query.unitType === 'base'
      const unitLabel = isBase ? '基本单位' : '销售单位'
      const unitCols = [
        { title: unitLabel, dataIndex: 'displayUnitName', width: 88, scopedSlots: { customRender: 'empty' } },
        { title: `${unitLabel}单价`, dataIndex: 'displayUnitPrice', width: 112, scopedSlots: { customRender: 'money' } },
        { title: `${unitLabel}数量`, dataIndex: 'displayQty', width: 112, scopedSlots: { customRender: 'qty' } },
        { title: `${unitLabel}金额`, dataIndex: 'displayAmount', width: 112, scopedSlots: { customRender: 'money' } }
      ]
      return fixed.concat(unitCols)
    }
  },
  created () {
    this.loadSupplierOptions()
    treeProductCategory().then(res => {
      this.categoryTreeRaw = this.normalizeTree((res && res.data) || [])
    }).catch(() => { this.categoryTreeRaw = [] })
    this.load()
  },
  methods: {
    normalizeTree (nodes) {
      return (nodes || []).map(n => ({
        id: n.id,
        categoryName: n.categoryName || n.name,
        children: this.normalizeTree(n.children || [])
      }))
    },
    toTreeSelectData (nodes) {
      return (nodes || []).map(n => ({
        title: n.categoryName,
        value: n.id,
        key: String(n.id),
        children: n.children && n.children.length ? this.toTreeSelectData(n.children) : undefined
      }))
    },
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatQty (value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      const s = n.toFixed(6)
      return s.replace(/\.?0+$/, '') || '0'
    },
    parseAmountTotal (data, records) {
      if (!data) return 0
      const raw = data.amountTotal != null ? data.amountTotal : data.amount_total
      if (raw != null && raw !== '') {
        const n = Number(raw)
        if (!Number.isNaN(n)) return n
      }
      if (records && records.length) {
        return records.reduce((sum, row) => sum + Number(this.lineGrossAmount(row) || 0), 0)
      }
      return 0
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    lineGrossAmount (row) {
      if (!row) return 0
      if (row.totalTaxAmount != null && row.totalTaxAmount !== '') return row.totalTaxAmount
      return Number(row.amount || 0) + Number(row.taxAmount || 0)
    },
    auditStatusText (status) {
      return ({ unaudited: '未审核', audited: '已审核', reverse_audited: '已反审核' })[status] || status || '—'
    },
    formatLineQty (row) {
      const unit = row.unitName || ''
      const qty = row.qtyBusiness != null ? Number(row.qtyBusiness) : 0
      return `${qty} ${unit}`.trim()
    },
    queryParams () {
      const specKeyword = (this.query.specKeyword || '').trim()
      return {
        supplierId: this.query.supplierId,
        categoryId: this.query.categoryId,
        customerKeyword: (this.query.customerKeyword || '').trim() || undefined,
        outboundNo: (this.query.outboundNo || '').trim() || undefined,
        specKeyword: specKeyword || undefined,
        dateStart: this.dateRange[0] ? this.dateRange[0].format('YYYY-MM-DD') : undefined,
        dateEnd: this.dateRange[1] ? this.dateRange[1].format('YYYY-MM-DD') : undefined,
        unitType: this.query.unitType || 'business',
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadSupplierOptions () {
      listCustomerSalesDetailSupplierOptions().then(res => {
        this.supplierOptions = res.data || []
      }).catch(() => { this.supplierOptions = [] })
    },
    load () {
      this.loading = true
      pageCustomerSalesDetailReport(this.queryParams()).then(res => {
        const data = res.data || {}
        const records = data.records || []
        this.rows = records
        this.pagination.total = Number(data.total || 0)
        this.amountTotal = this.parseAmountTotal(data, records)
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.dateRange = []
      this.search()
    },
    onPageChange (page) {
      this.pagination.current = page.current
      this.pagination.pageSize = page.pageSize
      this.load()
    },
    openOutboundDrawer (record) {
      if (!record || record.outboundId == null) return
      this.outboundDrawerVisible = true
      this.outboundLoading = true
      this.outboundForm = { items: [] }
      getSalesOutbound(record.outboundId).then(res => {
        this.outboundForm = res.data || { items: [] }
        this.outboundForm.items = this.outboundForm.items || []
      }).catch(err => {
        this.$message.error((err && err.message) || '加载出库单失败')
        this.closeOutboundDrawer()
      }).finally(() => { this.outboundLoading = false })
    },
    closeOutboundDrawer () {
      this.outboundDrawerVisible = false
      this.outboundForm = { items: [] }
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportCustomerSalesDetailReport(params)
        const blob = res && res.data !== undefined ? res.data : res
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `客商销售明细_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.customer-sales-detail-link {
  word-break: break-all;
}

.customer-sales-detail-amount-total {
  margin-right: 4px;
  color: #526071;
  font-size: 13px;
  strong {
    color: #225fae;
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
}
</style>
